import { BLOCKED_ITEM_FORM_CONDITIONS } from '@exam-center/blocked-user-management';
import {
  BlockedRecordBlockedScope,
  BlockedRecordBlockedType,
  BlockedUserIdType,
} from '@exam-center/graphql';
import { ID_CARD_NUMBER_MASK, PHONE_NUMBER_MASK } from '@exam-center/utils';
import {
  dayjs,
  englishTitleOptionSchema,
  isThaiTitle,
  thaiTitleOptionSchema,
  TITLE_REQUIRED_ERROR_MESSAGE,
} from '@shared/utils';
import IMask from 'imask';
import { z } from 'zod';

let superRefineCtx: z.RefinementCtx | null = null;

const BaseBlockedUserCreationSchema = z.object({
  personal: z.object({
    idType: z.nativeEnum(BlockedUserIdType),
    idNumber: z.string().trim().nullish(),
    // Thai title for CITIZEN_CARD, English title for PASSPORT — see validateTitle.
    title: z.union([thaiTitleOptionSchema, englishTitleOptionSchema]).nullish(),
    firstName: z.string().trim().nullish(),
    middleName: z.string().trim().nullish(),
    lastName: z.string().trim().nullish(),
    email: z.string().trim().nullish(),
    phoneNumber: z.string().nullish(),
  }),
  blocklistInfo: z.object({
    position: z.string().trim().nullish(),
    department: z.string().trim().nullish(),
    reason: z.string().trim().nullish(),
    blockedType: z
      .object({
        label: z.string(),
        value: z.nativeEnum(BlockedRecordBlockedType),
      })
      .nullish(),
    blockedAt: z.date().nullish(),
    examCenter: z.object({ label: z.string(), value: z.string() }).nullish(),
    blockedCourses: z
      .object({
        type: z.nativeEnum(BlockedRecordBlockedScope).nullish(),
        courses: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
      })
      .nullish(),
    enforcementEnd: z
      .object({
        assign: z
          .union([z.literal('true'), z.literal('false')])
          .transform((val) => val === 'true')
          .nullish(),
        duration: z
          .object({
            value: z.union([z.number(), z.literal('custom')]),
            label: z.string(),
          })
          .nullish(),
        date: z.date().nullish(),
      })
      .nullish(),
    examSessionInfo: z
      .object({
        round: z.number().nullish(),
        examDate: z.date().nullish(),
        startedAt: z.date().nullish(),
        endedAt: z.date().nullish(),
      })
      .nullish(),
    fileAttachments: z.array(z.instanceof(File)).nullish(),
  }),
});

type BaseBlockedUserCreationFormType = z.infer<typeof BaseBlockedUserCreationSchema>;

function validateIdNumber(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }
  const idNumber = data.personal.idNumber;
  const isPassport = data.personal.idType === BlockedUserIdType.Passport;

  if (isPassport) {
    if (!idNumber || idNumber.length < BLOCKED_ITEM_FORM_CONDITIONS.MIN_REQUIRED_FIELD_LENGTH) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'กรุณากรอกเลขที่พาสปอร์ต',
        path: ['personal', 'idNumber'],
      });
    }
    return;
  }

  if (!idNumber || idNumber.length < BLOCKED_ITEM_FORM_CONDITIONS.MIN_REQUIRED_FIELD_LENGTH) {
    return superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณากรอกเลขบัตรประชาชน',
      path: ['personal', 'idNumber'],
    });
  }

  const mask = IMask.createMask({ mask: ID_CARD_NUMBER_MASK });
  mask.resolve(idNumber);
  if (!mask.isComplete) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'รูปแบบเลขบัตรประชาชนไม่ถูกต้อง ต้องมีจำนวน 13 หลักและเป็นตัวเลขเท่านั้น',
      path: ['personal', 'idNumber'],
    });
  }
}

function validateTitle(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const title = data.personal.title;

  if (!title) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: TITLE_REQUIRED_ERROR_MESSAGE,
      path: ['personal', 'title'],
    });
    return;
  }

  const isPassport = data.personal.idType === BlockedUserIdType.Passport;
  const isThaiTitleValue = isThaiTitle(title.value);

  if (isPassport === isThaiTitleValue) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: TITLE_REQUIRED_ERROR_MESSAGE,
      path: ['personal', 'title'],
    });
  }
}

function validateFirstName(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const firstName = data.personal.firstName;

  if (!firstName || firstName.length < BLOCKED_ITEM_FORM_CONDITIONS.MIN_REQUIRED_FIELD_LENGTH) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณากรอกชื่อ',
      path: ['personal', 'firstName'],
    });
    return;
  }

  if (firstName.length > BLOCKED_ITEM_FORM_CONDITIONS.MAX_NAME_LENGTH) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `ความยาวต้องไม่เกิน ${BLOCKED_ITEM_FORM_CONDITIONS.MAX_NAME_LENGTH} ตัวอักษร`,
      path: ['personal', 'firstName'],
    });
  }
}

function validateLastName(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const lastName = data.personal.lastName;

  if (!lastName || lastName.length < BLOCKED_ITEM_FORM_CONDITIONS.MIN_REQUIRED_FIELD_LENGTH) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณากรอกนามสกุล',
      path: ['personal', 'lastName'],
    });
    return;
  }

  if (lastName.length > BLOCKED_ITEM_FORM_CONDITIONS.MAX_NAME_LENGTH) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `ความยาวต้องไม่เกิน ${BLOCKED_ITEM_FORM_CONDITIONS.MAX_NAME_LENGTH} ตัวอักษร`,
      path: ['personal', 'lastName'],
    });
  }
}

function validateEmail(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const email = data.personal.email;

  if (!email || email.length < BLOCKED_ITEM_FORM_CONDITIONS.MIN_REQUIRED_FIELD_LENGTH) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณากรอกอีเมลผู้ใช้',
      path: ['personal', 'email'],
    });
    return;
  }

  if (email.length > BLOCKED_ITEM_FORM_CONDITIONS.MAX_EMAIL_LENGTH) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `ความยาวต้องไม่เกิน ${BLOCKED_ITEM_FORM_CONDITIONS.MAX_EMAIL_LENGTH} ตัวอักษร`,
      path: ['personal', 'email'],
    });
    return;
  }

  if (!z.string().email().safeParse(email).success) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'รูปแบบอีเมลไม่ถูกต้อง',
      path: ['personal', 'email'],
    });
  }
}

function validatePhoneNumber(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const phoneNumber = data.personal.phoneNumber;

  if (phoneNumber) {
    const mask = IMask.createMask({ mask: PHONE_NUMBER_MASK });
    mask.resolve(phoneNumber);
    if (!mask.isComplete) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง',
        path: ['personal', 'phoneNumber'],
      });
    }
  }
}

function validateBlockedType(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const blockedType = data.blocklistInfo.blockedType;

  if (!blockedType) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณาเลือกประเภทหน้าที่',
      path: ['blocklistInfo', 'blockedType'],
    });
  }
}

function validateBlockedAt(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const blockedAt = data.blocklistInfo.blockedAt;

  if (!blockedAt) {
    const errorMessage =
      data.blocklistInfo.blockedType?.value !== BlockedRecordBlockedType.Cheater
        ? 'ระบุวันที่เริ่มปฏิบัติหน้าที่'
        : 'ระบุวันที่เริ่มระงับสิทธิ์';

    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: errorMessage,
      path: ['blocklistInfo.blockedAt'],
    });
  }
}

function validateEnforcementEnd(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const isCheaterType = data.blocklistInfo.blockedType?.value === BlockedRecordBlockedType.Cheater;
  const { enforcementEnd, blockedAt } = data.blocklistInfo;
  const blockedAtDate = dayjs(blockedAt);
  const enforcementEndDate = dayjs(enforcementEnd?.date ?? null);

  if (isCheaterType) {
    if (!enforcementEndDate.isValid()) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ระบุวันที่สิ้นสุดระงับสิทธิ์',
        path: ['blocklistInfo', 'enforcementEnd', 'date'],
      });
      return;
    }

    if (!enforcementEndDate.isAfter(blockedAtDate, 'date')) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'วันที่ต้องมากกว่าวันที่เริ่มระงับสิทธิ์',
        path: ['blocklistInfo', 'enforcementEnd', 'date'],
      });
    }
    return;
  }

  if (enforcementEnd?.assign === undefined || enforcementEnd?.assign === null) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณากำหนดวันที่สิ้นสุดปฏิบัติหน้าที่',
      path: ['blocklistInfo', 'enforcementEnd', 'assign'],
    });
    return;
  }

  if (enforcementEnd?.assign && !enforcementEnd?.duration) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณาเลือกระยะเวลาปฏิบัติหน้าที่',
      path: ['blocklistInfo', 'enforcementEnd', 'duration'],
    });
    return;
  }

  if (
    blockedAt &&
    enforcementEndDate.isValid() &&
    enforcementEnd.duration?.value === 'custom' &&
    !enforcementEndDate.isAfter(blockedAtDate, 'date')
  ) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'วันที่ต้องมากกว่าวันที่เริ่มปฏิบัติหน้าที่',
      path: ['blocklistInfo', 'enforcementEnd', 'date'],
    });
  }
}

function validateBlockedCourses(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  if (!data.blocklistInfo.blockedCourses?.type) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณากำหนดหลักสูตรที่ระงับสิทธิ์สอบ',
      path: ['blocklistInfo', 'blockedCourses', 'type'],
    });
    return;
  }

  if (data.blocklistInfo.blockedCourses?.type === BlockedRecordBlockedScope.PartialScope) {
    if (
      !data.blocklistInfo.blockedCourses.courses ||
      data.blocklistInfo.blockedCourses.courses.length === 0
    ) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'กรุณาเลือกหลักสูตรที่ระงับสิทธิ์สอบ',
        path: ['blocklistInfo', 'blockedCourses', 'courses'],
      });
    }
  }
}

function validateExamCenter(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const isRequired = data.blocklistInfo.blockedType?.value === BlockedRecordBlockedType.Cheater;
  const examCenter = data.blocklistInfo.examCenter;

  if (isRequired && !examCenter) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'กรุณาเลือกศูนย์ทดสอบ',
      path: ['blocklistInfo', 'examCenter'],
    });
  }
}

function validateExamSessionInfo(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const isCheaterType = data.blocklistInfo.blockedType?.value === BlockedRecordBlockedType.Cheater;
  const { examSessionInfo } = data.blocklistInfo;

  if (isCheaterType) {
    if (!examSessionInfo?.round || isNaN(Number(examSessionInfo?.round))) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'กรุณากรอกครั้งที่สอบ',
        path: ['blocklistInfo', 'examSessionInfo', 'round'],
      });
    }

    if (!examSessionInfo?.examDate) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ระบุวันที่สอบ',
        path: ['blocklistInfo', 'examSessionInfo', 'examDate'],
      });
    }

    if (!examSessionInfo?.startedAt) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ระบุเวลาเริ่มต้น',
        path: ['blocklistInfo', 'examSessionInfo', 'startedAt'],
      });
    }

    if (!examSessionInfo?.endedAt) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ระบุเวลาสิ้นสุด',
        path: ['blocklistInfo', 'examSessionInfo', 'endedAt'],
      });
    }
  }
}

function validateReason(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const reason = data.blocklistInfo.reason;

  if (reason && reason.length > BLOCKED_ITEM_FORM_CONDITIONS.MAX_REASON_LENGTH) {
    superRefineCtx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `ความยาวต้องไม่เกิน ${BLOCKED_ITEM_FORM_CONDITIONS.MAX_REASON_LENGTH} ตัวอักษร`,
      path: ['blocklistInfo', 'reason'],
    });
  }
}

function validateFileAttachments(data: BaseBlockedUserCreationFormType) {
  if (!superRefineCtx) {
    console.error('BaseBlockedUserCreationSchema: superRefineCtx is not defined');
    return;
  }

  const isRequired = data.blocklistInfo.blockedType?.value === BlockedRecordBlockedType.Cheater;
  const fileAttachments = data.blocklistInfo.fileAttachments;

  if (isRequired && fileAttachments) {
    if (fileAttachments.length > BLOCKED_ITEM_FORM_CONDITIONS.MAX_FILE_ATTACHMENTS) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `สามารถแนบไฟล์ได้ไม่เกิน ${BLOCKED_ITEM_FORM_CONDITIONS.MAX_FILE_ATTACHMENTS} ไฟล์`,
        path: ['blocklistInfo', 'fileAttachments'],
      });
      return;
    }

    if (
      fileAttachments.some(
        (file) => !BLOCKED_ITEM_FORM_CONDITIONS.ALLOW_FILE_EXTENSIONS.includes(file.type),
      )
    ) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'รูปแบบไฟล์ไม่ถูกต้อง กรุณาอัปโหลดไฟล์ .jpg หรือ .jpeg หรือ .png หรือ .pdf เท่านั้น',
        path: ['blocklistInfo', 'fileAttachments'],
      });
      return;
    }

    const totalSize = fileAttachments.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > BLOCKED_ITEM_FORM_CONDITIONS.MAX_TOTAL_ATTACH_FILE_SIZE_IN_BYTES) {
      superRefineCtx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `ขนาดไฟล์รวมกันต้องไม่เกิน ${BLOCKED_ITEM_FORM_CONDITIONS.MAX_TOTAL_ATTACH_FILE_SIZE_IN_MB} MB`,
        path: ['blocklistInfo', 'fileAttachments'],
      });
      return;
    }
  }
}

export const BlockedUserCreationSchema = BaseBlockedUserCreationSchema.superRefine((data, ctx) => {
  superRefineCtx = ctx;
  validateIdNumber(data);
  validateTitle(data);
  validateFirstName(data);
  validateLastName(data);
  validatePhoneNumber(data);
  validateEmail(data);
  validateBlockedType(data);
  validateBlockedAt(data);
  validateEnforcementEnd(data);
  validateBlockedCourses(data);
  validateExamCenter(data);
  validateReason(data);
  validateExamSessionInfo(data);
  validateFileAttachments(data);
});

export type BlockedUserCreationFormType = z.infer<typeof BlockedUserCreationSchema>;
